import React, { useState, useCallback, useEffect } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Pagination,
  Switch,
  FormControlLabel,
  Checkbox,
} from '@mui/material'
import {
  Add as AddIcon,
  Search as SearchIcon,
  ContentCopy as CopyIcon,
  LocalOffer as DiscountIcon,
  EditOutlined as EditIcon,
  VisibilityOutlined as ShowIcon,
} from '@mui/icons-material'
import { TableSortLabel } from '@mui/material'
import {
  useList,
  useUpdate,
  useNotification,
  useCustomMutation,
} from '@refinedev/core'
import { format, set } from 'date-fns'
import CreateDiscountCodeDialog from './CreateDiscountCodeDialog'
import EditDiscountCodeDialog from './EditDiscountCodeDialog'
import { DiscountCode } from '@/interfaces/discountCodes'
import ShowDiscountCodeDialog from './ShowDiscountCodeDialog'

interface DiscountCodesManagementProps {
  onDataChange?: () => void
  refreshTrigger?: number
}

type FilterType = 'all' | 'active' | 'expired' | 'archived' | 'exhausted'

export default function DiscountCodesManagement({
  onDataChange,
  refreshTrigger,
}: DiscountCodesManagementProps) {
  const { open } = useNotification()
  const [searchTerm, setSearchTerm] = useState('')
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [editingDiscountCode, setEditingDiscountCode] =
    useState<DiscountCode | null>()
  const [showDialogOpen, setShowDialogOpen] = useState(false)
  const [showingDiscountCode, setShowingDiscountCode] =
    useState<DiscountCode | null>()
  const [showArchived, setShowArchived] = useState(false)
  const [filter, setFilter] = useState<FilterType>('all')
  const [sortBy, setSortBy] = useState<string>('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [bulkArchive, setBulkArchive] = useState(false)
  const [codeIdsToArchive, setCodeIdsToArchive] = useState<number[]>([])
  const [bulkArchiving, setBulkArchiving] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  const { mutate: bulkArchiveMutate, isLoading: bulkArchiveLoading } =
    useCustomMutation()

  const handleSort = (field: string) => {
    if (sortBy === field) {
      if (sortOrder === 'desc') {
        setSortOrder('asc')
      } else {
        // Reset to default sorting
        setSortBy('created_at')
        setSortOrder('desc')
      }
    } else {
      setSortBy(field)
      setSortOrder('desc')
    }
  }

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ): void => {
    setCurrentPage(page)
  }

  // Fetch discount codes
  const { data, isLoading, refetch } = useList<DiscountCode>({
    resource: 'discount-codes',
    pagination: {
      current: currentPage,
      pageSize: pageSize,
    },
    filters: [],
    meta: {
      ...(searchTerm && { search: searchTerm.trim() }),
      filter: filter,
      sort_by: sortBy,
      sort_order: sortOrder,
      show_archived: showArchived,
    },
    queryOptions: {
      queryKey: [
        'discount-codes',
        searchTerm,
        filter,
        sortBy,
        sortOrder,
        showArchived,
        currentPage,
      ],
    },
  })

  const discountCodes = data?.data || []
  const total = data?.total || 0
  const totalPages = Math.ceil(total / pageSize)

  useEffect(() => {
    if (filter === 'archived') {
      setShowArchived(true)
    }
  }, [filter])

  // Refetch when refresh trigger changes
  useEffect(() => {
    if (refreshTrigger !== undefined) {
      refetch()
    }
  }, [refreshTrigger, refetch])

  function handleEdit(code: DiscountCode) {
    setEditDialogOpen(true)
    setEditingDiscountCode(code)
  }
  function handleShow(code: DiscountCode) {
    setShowDialogOpen(true)
    setShowingDiscountCode(code)
  }

  async function handleBulkArchive() {
    if (!codeIdsToArchive || codeIdsToArchive.length === 0) {
      return
    }

    setBulkArchiving(true)

    await bulkArchiveMutate(
      {
        url: `/discount-codes/bulk-archive`,
        method: 'patch',
        values: {
          ids: codeIdsToArchive,
        },
      },
      {
        onSuccess: () => {
          setCodeIdsToArchive([])
          open?.({
            type: 'success',
            message: 'Discount codes archived successfully',
          })
        },
        onError: () => {
          open?.({
            message: 'Failed to archive discount codes',
            type: 'error',
          })
        },
      }
    )

    setBulkArchiving(false)
    setBulkArchive(false)
  }

  const handleCopyCode = useCallback(
    (code: string) => {
      navigator.clipboard.writeText(code)
      open?.({
        type: 'success',
        message: 'Discount code copied to clipboard',
      })
    },
    [open]
  )

  const handleCreateSuccess = useCallback(() => {
    setCreateDialogOpen(false)
    refetch()
    onDataChange?.()
    open?.({
      type: 'success',
      message: 'Discount code created successfully',
    })
  }, [refetch, open, onDataChange])

  const handleEditSuccess = useCallback(() => {
    setEditDialogOpen(false)
    refetch()
    onDataChange?.()
    open?.({
      type: 'success',
      message: 'Discount code updated successfully',
    })
  }, [refetch, open, onDataChange])

  const formatDiscountValue = (_type: string, value: number) => {
    return `${value}%`
  }

  const getStatusChip = (code: DiscountCode) => {
    const now = new Date()
    const validFrom = new Date(code.valid_from)
    const validUntil = new Date(code.valid_until)

    if (!code.active) {
      return <Chip label="Inactive" color="default" size="small" />
    }

    if (now < validFrom) {
      return <Chip label="Scheduled" color="info" size="small" />
    }

    if (now > validUntil) {
      return <Chip label="Expired" color="warning" size="small" />
    }

    if (code.max_uses && code.current_uses >= code.max_uses) {
      return <Chip label="Exhausted" color="warning" size="small" />
    }

    return <Chip label="Active" color="success" size="small" />
  }

  const getUsageDisplay = (code: DiscountCode) => {
    if (!code.max_uses) {
      return `${code.current_uses} used`
    }
    return `${code.current_uses} / ${code.max_uses}`
  }

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <DiscountIcon color="primary" />
              <Typography variant="h5" fontWeight={600}>
                Discount Codes
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
            >
              Create Discount Code
            </Button>
          </Box>

          {/* Search Bar */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              sx={{ width: '50%' }}
              placeholder="Search by code or description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl sx={{ mb: 2, width: '150px' }}>
              <InputLabel id="filter-label">Filter</InputLabel>
              <Select
                value={filter}
                onChange={(e) => setFilter(e.target.value as FilterType)}
                sx={{ mb: 3, width: '150px' }}
                labelId="filter-label"
                label="Filter"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
                <MenuItem value="exhausted">Exhausted</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box
            sx={{
              display: 'flex',
              width: '100%',
              justifyContent: 'space-between',
              mb: 1,
            }}
          >
            {filter !== 'active' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={showArchived}
                    onChange={() => setShowArchived(!showArchived)}
                  />
                }
                label="Show Archived"
              />
            )}
            <Box sx={{ display: 'flex', gap: 2, flexDirection: 'column' }}>
              <Button
                variant="outlined"
                onClick={() => setBulkArchive(!bulkArchive)}
              >
                {bulkArchive ? 'Cancel Bulk Archive' : 'Start Bulk Archive'}
              </Button>
              {bulkArchive && codeIdsToArchive.length > 0 && (
                <Button
                  variant="contained"
                  onClick={() => handleBulkArchive()}
                  size="small"
                  sx={{ textTransform: 'none' }}
                >
                  {bulkArchiving
                    ? 'Archiving...'
                    : `Bulk Archive Selected (${codeIdsToArchive.length})`}
                </Button>
              )}
            </Box>
          </Box>

          {/* Discount Codes Table */}
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Discount</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'created_at'}
                      direction={sortBy === 'created_at' ? sortOrder : 'desc'}
                      onClick={() => handleSort('created_at')}
                    >
                      Created
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'valid_until'}
                      direction={sortBy === 'valid_until' ? sortOrder : 'desc'}
                      onClick={() => handleSort('valid_until')}
                    >
                      Valid Period
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'current_uses'}
                      direction={sortBy === 'current_uses' ? sortOrder : 'desc'}
                      onClick={() => handleSort('current_uses')}
                    >
                      Usage
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                  {bulkArchive && (
                    <TableCell align="center">Bulk Archive</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {discountCodes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No discount codes found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  discountCodes.map((code) => (
                    <TableRow key={code.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" fontFamily="monospace">
                            {code.code}
                          </Typography>
                          <Tooltip title="Copy code">
                            <IconButton
                              size="small"
                              onClick={() => handleCopyCode(code.code)}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap maxWidth={200}>
                          {code.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDiscountValue(
                            code.discount_type,
                            code.discount_value
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {format(new Date(code.created_at), 'MMM d, yyyy')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {format(new Date(code.valid_from), 'MMM d, yyyy')} -
                          <br />
                          {format(new Date(code.valid_until), 'MMM d, yyyy')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {code.duration_months} month
                          {code.duration_months !== 1 ? 's' : ''}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getUsageDisplay(code)}
                        </Typography>
                      </TableCell>
                      <TableCell>{getStatusChip(code)}</TableCell>
                      <TableCell align="center" sx={{ px: 0, maxWidth: 50 }}>
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <IconButton onClick={() => handleEdit(code)}>
                            <EditIcon />
                          </IconButton>
                          <IconButton onClick={() => handleShow(code)}>
                            <ShowIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                      {bulkArchive && !code.archived && (
                        <TableCell align="center">
                          {' '}
                          <Checkbox
                            checked={codeIdsToArchive.includes(code.id)}
                            onChange={(e) =>
                              setCodeIdsToArchive(
                                e.target.checked
                                  ? [...codeIdsToArchive, code.id]
                                  : codeIdsToArchive.filter(
                                      (id) => id !== code.id
                                    )
                              )
                            }
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}

          {/* Summary Stats */}
          {discountCodes.length > 0 && (
            <Box mt={3} display="flex" gap={3}>
              <Alert severity="info" sx={{ flex: 1 }}>
                <Typography variant="body2">
                  <strong>Active Codes:</strong>{' '}
                  {discountCodes.filter((c) => c.active).length}
                </Typography>
              </Alert>
              <Alert severity="info" sx={{ flex: 1 }}>
                <Typography variant="body2">
                  <strong>Total Uses:</strong>{' '}
                  {discountCodes.reduce((sum, c) => sum + c.current_uses, 0)}
                </Typography>
              </Alert>
              <Alert severity="info" sx={{ flex: 1 }}>
                <Typography variant="body2">
                  <strong>Average Discount:</strong>{' '}
                  {Math.round(
                    discountCodes.reduce(
                      (sum, c) => sum + c.discount_value,
                      0
                    ) / discountCodes.length
                  )}
                  %
                </Typography>
              </Alert>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Create Discount Code Dialog */}
      <CreateDiscountCodeDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCreateSuccess}
      />
      {/* Edit Discount Code Dialog */}
      {editDialogOpen && editingDiscountCode && (
        <EditDiscountCodeDialog
          discountCode={editingDiscountCode}
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onSuccess={handleEditSuccess}
        />
      )}
      {/* Show Discount Code Dialog */}
      {showDialogOpen && showingDiscountCode && (
        <ShowDiscountCodeDialog
          discountCode={showingDiscountCode}
          open={showDialogOpen}
          onClose={() => setShowDialogOpen(false)}
        />
      )}
    </Box>
  )
}
